/**
 * `wayai bases batch` — atomic multi-operation writes against one base.
 *
 * Namespaced because `batch` is too generic to stand alone on a CLI that
 * already batches several unrelated things
 * (docs/reference/rekor-platform-contracts.md §3.2).
 */

import { Command } from 'commander';
import { createDataClient } from '../client.js';
import { printBatchResult, printOutput } from '../output.js';
import { outputFormat, parseData, pathSegment, requireBase, withBaseOption } from '../helpers.js';

export function buildBasesBatchCommand(): Command {
  // Declares its own `--base`: the `bases` namespace root deliberately has none
  // (its own verbs name the base positionally), and this command addresses one.
  return withBaseOption(new Command('batch'))
    .description('Execute atomic batch operations (up to 1,000 operations)')
    .requiredOption('--operations <json>', 'Operations array (inline JSON or @filename)')
    .action(async function (this: Command, opts: { operations: string }) {
      const base = pathSegment(requireBase(this), '--base');
      const operations = parseData(opts.operations, '--operations');
      const client = await createDataClient();
      const data = await client.request('POST', `/v1/${base}/batch`, {
        operations: Array.isArray(operations) ? operations : [operations],
      });
      // The batch body is `{ batch_id, results: [...] }` — an object, so the
      // generic renderer would cram the whole results array into one unreadable
      // cell. Print a per-operation summary instead; `--json` still yields the
      // full payload.
      const format = outputFormat(this);
      if (format === 'json') {
        printOutput(data, format);
        return;
      }
      printBatchResult(data);
    });
}
